/**
 * LandingPage.mjs
 *
 * Dedicated landing page for the root route (/). Provides a modern,
 * visually compelling introduction to the platform with two modes:
 *
 *   1. Guest mode — large hero + feature showcase + article previews + CTA
 *   2. Authenticated mode — welcome header + quick links + article previews
 *
 * SSR-safe: uses useIsMounted() guard for all browser-only code paths.
 * All navigation uses SafeLink (renders <a> on server, <Link> on client)
 * because the SSR render tree has no router context.
 */
import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchBulkRatingAggregates } from '../slices/ratingsSlice.mjs';
import useIsMounted from '../hooks/useIsMounted.mjs';
import { useLandingArticles } from '../hooks/useLandingArticles.mjs';
import { buildArticleUrl } from '../utils/buildArticleUrl.mjs';
import HomePageContent from './body/Home.mjs';
import HomePage from './HomePage.mjs';

/* ── SSR-safe link (mirrors ArticleLinkWrapper pattern) ────── */

function SafeLink({ to, className, children }) {
    const isMounted = useIsMounted();
    if (isMounted) {
        return <Link to={to} className={className}>{children}</Link>;
    }
    return <a href={to} className={className}>{children}</a>;
}

/* ── Inline SVG Icon Components ────────────────────────────────── */

function SparkleIcon() {
    return (
        <svg className="lp-feature__icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3l1.912 5.813a2 2 0 001.275 1.275L21 12l-5.813 1.912a2 2 0 00-1.275 1.275L12 21l-1.912-5.813a2 2 0 00-1.275-1.275L3 12l5.813-1.912a2 2 0 001.275-1.275L12 3z" />
        </svg>
    );
}

function ChatBubbleIcon() {
    return (
        <svg className="lp-feature__icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
            <path d="M8 9h8M8 13h5" />
        </svg>
    );
}

function NetworkIcon() {
    return (
        <svg className="lp-feature__icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="5" r="2" />
            <circle cx="5" cy="19" r="2" />
            <circle cx="19" cy="19" r="2" />
            <path d="M12 7v4M8.5 16.5L11 13M15.5 16.5L13 13" />
            <circle cx="12" cy="13" r="1.5" />
        </svg>
    );
}

function ArrowRightIcon({ className }) {
    return (
        <svg className={className || 'lp-cta__arrow'} viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
            <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
    );
}

/* ── Feature Card Data ─────────────────────────────────────────── */

const FEATURES = [
    {
        id: 'for-you',
        Icon: SparkleIcon,
        title: 'Your Personal News Feed',
        link: '/for-you',
        showArrow: true,
    },
    {
        id: 'conversations',
        Icon: ChatBubbleIcon,
        title: 'Start a Conversation',
        link: '/signup',
        showArrow: true,
    },
];

/* ── Feature Pill Component ───────────────────────────────────── */

function FeaturePill({ feature, index }) {
    const content = (
        <div className={`lp-feature-pill lp-feature-pill--${feature.id}`} style={{ '--feature-index': index }}>
            <div className="lp-feature-pill__icon">
                <feature.Icon />
            </div>
            <span className="lp-feature-pill__label">{feature.title}</span>
            {feature.showArrow && <ArrowRightIcon className="lp-feature-pill__arrow" />}
        </div>
    );

    if (feature.link) {
        return <SafeLink to={feature.link} className="lp-feature-pill__anchor">{content}</SafeLink>;
    }
    return content;
}

/* ── Main Component ───────────────────────────────────────────── */

export default function LandingPage() {
    const isMounted = useIsMounted();
    const dispatch = useDispatch();
    const auth = useSelector((state) => state.auth);
    const { articles: articlesGroupedByCategory, isLoading } = useLandingArticles();

    const isAuthenticated = auth?.isAuthenticated;
    const userName = auth?.user?.name || auth?.user?.displayName || '';
    const firstName = userName ? userName.split(' ')[0] : '';

    // Extract all article IDs for bulk rating loading
    const allArticleIds = useMemo(() => {
        if (!Array.isArray(articlesGroupedByCategory)) return [];
        const ids = articlesGroupedByCategory.flatMap((group) =>
            group && Array.isArray(group.articles)
                ? group.articles.map((article) => article?._id).filter(Boolean)
                : []
        );
        return [...new Set(ids)];
    }, [articlesGroupedByCategory]);

    // Rating aggregates — thunk condition + body handle staleness/dedup
    useEffect(() => {
        if (allArticleIds.length > 0 && !isLoading) {
            dispatch(fetchBulkRatingAggregates({ articleIds: allArticleIds }));
        }
    }, [allArticleIds, dispatch, isLoading]);


    // Legacy preference: when user has contentExperience set to 'default',
    // render the classic HomePage. This must mirror the SSR controller
    // (landingPageViewController) which delegates to homeViewController
    // for the same preference to avoid hydration mismatch.
    const contentExperience = auth?.user?.preferences?.contentExperience;
    if (contentExperience === 'default') {
        return <HomePage />;
    }

    // ── Authenticated experience ────────────────────────────
    if (isAuthenticated) {
        /* Derive a deep-link to the first available article with chat open */
        const firstArticle = articlesGroupedByCategory?.[0]?.articles?.[0] ?? null;
        const chatLink = firstArticle
            ? `${buildArticleUrl(firstArticle)}?chat=open`
            : '/for-you';

        /* Feature pills with auth-adjusted links */
        const AUTH_FEATURES = [
            {
                ...FEATURES[0],
            },
            {
                ...FEATURES[1],
                link: chatLink,
            },
        ];

        const shareUrl = isMounted ? window.location.origin : 'https://nexusoftruth.com';
        const shareText = 'Check out Nexus of Truth — AI-powered multi-source sports journalism ⚡';
        const xShareHref = `https://x.com/intent/post?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;

        return (
            <div className="lp">
                {/* Enhanced welcome with subtle gradient */}
                <section className="lp-welcome lp-welcome--enhanced">
                    <div className="lp-welcome__bg" aria-hidden="true">
                        <div className="lp-welcome__orb lp-welcome__orb--1" />
                        <div className="lp-welcome__orb lp-welcome__orb--2" />
                    </div>

                    <div className="lp-welcome__inner">
                        <h1 className="lp-welcome__heading">
                            Welcome back{firstName ? `, ${firstName}` : ''}
                        </h1>
                        <p className="lp-welcome__sub">
                            Here's what's new across the platform.
                        </p>

                        {/* Quick action pills */}
                        <div className="lp-welcome__actions">
                            <SafeLink to="/for-you" className="lp-pill lp-pill--primary">
                                <SparkleIcon /> For You
                            </SafeLink>
                            <SafeLink to={chatLink} className="lp-pill lp-pill--secondary">
                                <ChatBubbleIcon /> Chat with AI
                            </SafeLink>
                            <a
                                href={xShareHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="lp-pill lp-pill--outline"
                            >
                                <svg className="lp-pill__icon-svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                </svg>
                                Share on X
                            </a>
                        </div>
                    </div>
                </section>


                {/* Article previews */}
                <section className="lp-articles">
                    <HomePageContent
                        articlesGroupedByCategory={articlesGroupedByCategory}
                    />
                </section>
            </div>
        );
    }

    // ── Guest experience ────────────────────────────────────
    return (
        <div className="lp">
            {/* Hero */}
            <section className="lp-hero">
                <div className="lp-hero__bg" aria-hidden="true">
                    <div className="lp-hero__orb lp-hero__orb--1" />
                    <div className="lp-hero__orb lp-hero__orb--2" />
                    <div className="lp-hero__orb lp-hero__orb--3" />
                </div>

                <div className="lp-hero__content">
                    <h1 className="lp-hero__heading">
                        <span className="lp-hero__heading-line">Discover the</span>
                        <span className="lp-hero__heading-accent">Truth</span>
                    </h1>
                    <p className="lp-hero__sub">
                        Multi-source journalism powered by AI. Personalized feeds,
                        contextual conversations, and cross-article intelligence — all
                        in one platform.
                    </p>
                    <div className="lp-hero__ctas">
                        <SafeLink to="/signup" className="lp-cta lp-cta--primary">
                            Get Started <ArrowRightIcon />
                        </SafeLink>
                        <a href="#latest-articles" className="lp-cta lp-cta--secondary">
                            Explore Articles
                        </a>
                    </div>
                </div>
            </section>


            {/* Article previews */}
            <section className="lp-articles" id="latest-articles">
                <div className="lp-articles__header">
                    <h2 className="lp-articles__heading">Latest Articles</h2>
                    <p className="lp-articles__sub">
                        Dive into our most recent stories across every category.
                    </p>
                </div>
                <HomePageContent
                    articlesGroupedByCategory={articlesGroupedByCategory}
                />
            </section>

            {/* Final CTA */}
            <section className="lp-final-cta">
                <div className="lp-final-cta__inner">
                    <h2 className="lp-final-cta__heading">Ready to see the full picture?</h2>
                    <p className="lp-final-cta__sub">
                        Create a free account to unlock personalized feeds, AI
                        conversations, and more.
                    </p>
                    <SafeLink to="/signup" className="lp-cta lp-cta--primary lp-cta--lg">
                        Join Nexus of Truth <ArrowRightIcon />
                    </SafeLink>
                </div>
            </section>
        </div>
    );
}
